import { SubscriptionPlanName } from '@sparemin/api-sdk';
import cn from 'classnames';
import * as React from 'react';

import { H3 } from 'components/Heading';
import { SubscriptionPeriod } from '../types';
import CostDisplay from './CostDisplay';
import { block } from './utils';

type PlanOptions = Record<SubscriptionPeriod, SubscriptionPlanName>;

export interface PlanCostProps {
  className?: string;
  density?: 'comfortable' | 'sparse';
  description?: string;
  period?: SubscriptionPeriod;
  cost: PlanOptions | string;
  previousCost?: Partial<PlanOptions> | string;
  title: React.ReactNode;
  theme?:
    | 'free' // all elements pink
    | 'basic' // all elements are the "basic" color
    | 'pro' // all elements are the "pro" color
    | 'enterprise' // all elements are green
    | 'pro-upsell' // gradient title, white price, gray footnote
    | 'pro-grow' // gradient title, white price, gray footnote
    | 'basic-upsell' // "basic" title, white price, "basic" footnote
    | 'muted' // all elements are gray
    | 'unlimited';
}

const PlanCost: React.FC<PlanCostProps> = ({
  className,
  period,
  cost,
  previousCost,
  density = 'sparse',
  description,
  theme,
  title,
}) => {
  const hasPreviousCost = !!previousCost;

  return (
    <div className={cn(block({ [theme]: !!theme }), className)}>
      <H3 className={block('title')}>{title}</H3>

      {description && (
        <p className={block('description', { [density]: true })}>
          {description}
        </p>
      )}

      <div
        className={block('cost', {
          centered: !hasPreviousCost,
          [period]: true,
        })}
      >
        <CostDisplay
          {...{ cost, period }}
          className={block({ [period]: true })}
        />

        {typeof cost !== 'string' && period === 'yearly' && (
          <span className={block('footnote')}>BILLED YEARLY</span>
        )}

        {hasPreviousCost && (
          <CostDisplay cost={previousCost} period={period} isPrevious />
        )}
      </div>
    </div>
  );
};

export default PlanCost;
